import { useMemo, useState } from "react"

import { IconCalendar } from "@tabler/icons-react"

import { Button } from "./Button"
import { Calendar } from "./Calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./Popover"
import { Select } from "./Select"

const ALL_TIMEZONE_OPTIONS = Intl.supportedValuesOf("timeZone").map((tz) => ({
  label: tz,
  value: tz,
}))

type TZParts = {
  year: number
  month: number // 0-indexed
  day: number
  hour: number
  minute: number
}

function extractPartsInTZ(date: Date, tz: string): TZParts {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
  const map = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]))
  return {
    year: parseInt(map.year),
    month: parseInt(map.month) - 1,
    day: parseInt(map.day),
    hour: parseInt(map.hour) % 24, // handle "24:00" edge case from some formatters
    minute: parseInt(map.minute),
  }
}

// Convert a local date/time in a given IANA timezone to a UTC Date.
function zonedToUTC(
  year: number,
  month0: number,
  day: number,
  hour: number,
  minute: number,
  tz: string,
): Date {
  // Treat the local time as UTC to get a starting guess, then correct for the offset.
  const guess = new Date(Date.UTC(year, month0, day, hour, minute))
  const p = extractPartsInTZ(guess, tz)
  const diffMs =
    Date.UTC(year, month0, day) -
    Date.UTC(p.year, p.month, p.day) +
    (hour * 60 + minute - (p.hour * 60 + p.minute)) * 60_000
  return new Date(guess.getTime() + diffMs)
}

export type DateTimePickerProps = {
  value?: Date
  onChange?: (date: Date | undefined) => void
  defaultTimezone?: string
  timezones?: string[]
  defaultHour?: number
  defaultMinute?: number
  required?: boolean
  disabled?: boolean
}

export function DateTimePicker({
  value,
  onChange,
  defaultTimezone = "America/Los_Angeles",
  timezones,
  defaultHour = 0,
  defaultMinute = 0,
  required,
  disabled,
}: DateTimePickerProps) {
  const [tz, setTz] = useState(defaultTimezone)

  const timezoneOptions = useMemo(
    () =>
      timezones
        ? timezones.map((tz) => ({ label: tz, value: tz }))
        : ALL_TIMEZONE_OPTIONS,
    [timezones],
  )

  const parts = value ? extractPartsInTZ(value, tz) : null

  // Calendar expects a local Date for `selected` — build one from TZ-extracted parts.
  const calendarDate = parts ? new Date(parts.year, parts.month, parts.day) : undefined

  const handleDaySelect = (day: Date | undefined) => {
    if (!day) {
      if (!required) onChange?.(undefined)
      return
    }
    onChange?.(
      zonedToUTC(
        day.getFullYear(),
        day.getMonth(),
        day.getDate(),
        parts?.hour ?? defaultHour,
        parts?.minute ?? defaultMinute,
        tz,
      ),
    )
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!parts) return
    const [h, m] = e.target.value.split(":").map(Number)
    if (isNaN(h) || isNaN(m)) return
    onChange?.(zonedToUTC(parts.year, parts.month, parts.day, h, m, tz))
  }

  const dateLabel = value
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(value)
    : undefined

  const tzOption = timezoneOptions.find((o) => o.value === tz) ?? null

  return (
    <div className="flex items-center gap-2">
      <div className="w-1/4">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              icon={IconCalendar}
              variant={value ? "secondary" : "neutral"}
              size="small"
              disabled={disabled}
              className="w-full"
            >
              {dateLabel ?? <span className="text-muted-foreground">Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent side="bottom" align="start" className="w-auto p-0">
            <Calendar
              mode="single"
              selected={calendarDate}
              onSelect={handleDaySelect}
              defaultMonth={calendarDate}
              fixedWeeks
            />
          </PopoverContent>
        </Popover>
      </div>
      <input
        type="time"
        value={
          parts
            ? `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`
            : ""
        }
        onChange={handleTimeChange}
        disabled={!value || disabled}
        className="w-1/4 rounded border border-input bg-transparent px-2 py-1 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
      />
      <div className="w-1/2">
        <Select
          isMulti={false}
          options={timezoneOptions}
          value={tzOption}
          onChange={(opt) => opt && setTz(opt.value)}
          menuPortalTarget={document.body}
          isDisabled={disabled}
          placeholder="Timezone..."
        />
      </div>
    </div>
  )
}
